import React, { useEffect, useState } from "react";
import { Crown, Link2, Mail, Trash2, UserPlus } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const roleBadge = {
  owner: "bg-slate-900 text-white",
  editor: "bg-brand-soft text-brand",
  viewer: "bg-slate-100 text-slate-500",
};

export default function TeamPage() {
  const { t, fmtDate } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ email: "", role: "editor" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/team").then((r) => setData(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/team/invite", form);
      toast.success(t.team.sent);
      setForm({ email: "", role: "editor" });
      load().catch(() => {});
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (memberId, role) => {
    await api.put(`/team/members/${memberId}`, { role }).catch((e) => toast.error(fmtErr(e)));
    load().catch(() => {});
  };

  const removeMember = async (memberId) => {
    if (!window.confirm(t.app.confirmDelete)) return;
    await api.delete(`/team/members/${memberId}`).catch((e) => toast.error(fmtErr(e)));
    load().catch(() => {});
  };

  const cancelInvite = async (inviteId) => {
    await api.delete(`/team/invites/${inviteId}`).catch((e) => toast.error(fmtErr(e)));
    load().catch(() => {});
  };

  const leave = async () => {
    if (!window.confirm(t.team.leaveConfirm)) return;
    await api.post("/team/leave").catch((e) => toast.error(fmtErr(e)));
    window.location.reload();
  };

  if (!data) return <p className="py-24 text-center text-sm text-slate-400">{t.app.loading}</p>;

  const isOwner = data.is_owner;

  return (
    <div className="space-y-6" data-testid="team-page">
      {!isOwner && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="team-membership-card">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-slate-900"><Crown className="h-5 w-5 text-white" /></div>
          <div>
            <p className="text-sm text-slate-500">{t.team.memberOf}</p>
            <p className="font-display text-base font-semibold text-slate-900">{data.owner_name}</p>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${roleBadge[user?.team_role] || roleBadge.viewer}`}>
            {t.team[user?.team_role] || user?.team_role}
          </span>
          <Button variant="outline" onClick={leave} data-testid="team-leave-button" className="ml-auto">{t.team.leave}</Button>
        </div>
      )}

      {isOwner && (
        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="team-invite-card">
          <p className="font-display text-sm font-semibold text-slate-900">{t.team.inviteTitle}</p>
          <form onSubmit={invite} className="mt-4 grid gap-3 sm:grid-cols-[1fr_180px_auto]">
            <div className="space-y-1.5">
              <Label className="sr-only">{t.app.email}</Label>
              <Input type="email" required placeholder="ornek@sirket.com" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-invite-email-input" />
            </div>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} data-testid="team-invite-role-select">
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="editor">{t.team.editor}</SelectItem>
                <SelectItem value="viewer">{t.team.viewer}</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={busy} data-testid="team-invite-submit-button">
              <UserPlus className="mr-2 h-4 w-4" />{t.team.send}
            </Button>
          </form>
          {error && <p className="mt-3 rounded-md border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-600" data-testid="team-invite-error">{error}</p>}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm" data-testid="team-members-table">
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="font-display text-sm font-semibold text-slate-900">{t.team.members}</p>
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-50">
            {data.members.map((m) => (
              <tr key={m.id} className="transition-colors hover:bg-slate-50" data-testid={`team-member-${m.id}`}>
                <td className="px-5 py-3.5">
                  <p className="font-medium text-slate-900">{m.name}{m.id === user?.id && <span className="ml-2 text-xs text-slate-400">({t.team.you})</span>}</p>
                  <p className="text-xs text-slate-400">{m.email}</p>
                </td>
                <td className="hidden px-5 py-3.5 font-mono text-xs text-slate-400 md:table-cell">
                  {m.joined_at ? fmtDate(String(m.joined_at).slice(0, 10)) : "—"}
                </td>
                <td className="px-5 py-3.5">
                  {isOwner && m.id !== user?.id && m.team_role !== "owner" ? (
                    <Select value={m.team_role} onValueChange={(v) => changeRole(m.id, v)} data-testid={`team-role-select-${m.id}`}>
                      <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="editor">{t.team.editor}</SelectItem>
                        <SelectItem value="viewer">{t.team.viewer}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${roleBadge[m.team_role] || roleBadge.viewer}`}>
                      {t.team[m.team_role] || m.team_role}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3.5 text-right">
                  {isOwner && m.id !== user?.id && (
                    <button onClick={() => removeMember(m.id)} aria-label={t.team.remove} data-testid={`team-remove-${m.id}`}
                      className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isOwner && data.invites.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm" data-testid="team-invites-table">
          <div className="border-b border-slate-100 px-5 py-4">
            <p className="font-display text-sm font-semibold text-slate-900">{t.team.invites}</p>
          </div>
          <div className="divide-y divide-slate-50">
            {data.invites.map((inv) => (
              <div key={inv.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-testid={`team-invite-${inv.id}`}>
                <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                <p className="text-sm text-slate-700">{inv.email}</p>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${roleBadge[inv.role]}`}>{t.team[inv.role]}</span>
                <div className="ml-auto flex items-center gap-1">
                  <button
                    onClick={() => { navigator.clipboard.writeText(inv.link); toast.success(t.team.copied); }}
                    aria-label={t.team.copyLink} data-testid={`team-invite-copy-${inv.id}`} title={inv.link}
                    className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900">
                    <Link2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => cancelInvite(inv.id)} aria-label={t.team.cancel} data-testid={`team-invite-cancel-${inv.id}`}
                    className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
